import { Badge } from "./ui/badge";
import { Label } from "./ui/label";
import { useT } from "../lib/i18n";
import { marginTone, pct } from "../lib/format";

export const PageHeader = ({ title, kicker, children }) => (
  <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
    <div>
      {kicker && <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#db0720]">{kicker}</div>}
      <h1 className="text-2xl font-bold tracking-tight text-black sm:text-3xl" data-testid="page-title">{title}</h1>
    </div>
    <div className="flex flex-wrap items-center gap-2">{children}</div>
  </div>
);

const STATUS_TONE = {
  draft: "bg-neutral-100 text-neutral-700 border-neutral-300", sent: "bg-sky-50 text-sky-700 border-sky-200",
  accepted: "bg-emerald-50 text-emerald-700 border-emerald-200", rejected: "bg-red-50 text-red-700 border-red-200",
  cancelled: "bg-neutral-200 text-neutral-500 border-neutral-300",
};

export const StatusBadge = ({ status, testid }) => {
  const { t } = useT();
  return <Badge variant="outline" data-testid={testid} className={`rounded-sm text-[11px] ${STATUS_TONE[status] || ""}`}>{t(`status.${status}`)}</Badge>;
};

export const MarginBadge = ({ value, testid }) =>
  value == null ? <span className="text-neutral-400">—</span> : (
    <span data-testid={testid} className={`inline-block rounded-sm border px-1.5 py-0.5 text-[11px] font-semibold num ${marginTone(value)}`}>{pct(value)}</span>
  );

export const Folio = ({ children }) => (
  <span className="rounded-sm border border-red-200 bg-red-50 px-2 py-0.5 font-mono text-xs font-bold tracking-wider text-[#db0720]">{children}</span>
);

export const Field = ({ label, required, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-xs font-semibold text-neutral-700">{label}{required && <span className="text-[#db0720]"> *</span>}</Label>
    {children}
  </div>
);

export const Section = ({ title, children, right, testid }) => (
  <section data-testid={testid} className="rounded-md border border-neutral-200 bg-white">
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-200 px-4 py-2.5">
      <h3 className="text-sm font-bold uppercase tracking-wider text-neutral-800">{title}</h3>
      {right && <div className="w-full sm:w-auto">{right}</div>}
    </div>
    <div className="p-3 sm:p-4">{children}</div>
  </section>
);

export const NativeSelect = ({ value, onChange, options, placeholder, testid, disabled, className = "" }) => (
  <select data-testid={testid} disabled={disabled} value={value ?? ""} onChange={(e) => onChange(e.target.value || null)}
    className={`h-9 w-full rounded border border-neutral-300 bg-white px-2 text-sm focus:border-[#db0720] focus:outline-none focus:ring-1 focus:ring-[#db0720] disabled:bg-neutral-50 ${className}`}>
    {placeholder !== undefined && <option value="">{placeholder}</option>}
    {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

export const Empty = ({ children }) => <div className="py-10 text-center text-sm text-neutral-500">{children}</div>;
